
import { Component, Vue, Prop, Watch, Emit, Mixins, Inject, Ref } from "vue-property-decorator";
import { namespace, Getter, Action, State } from "vuex-class";
import { validationMixin } from "vuelidate";
import { required } from "vuelidate/lib/validators";
import { get } from "lodash";

import api from "@/services/api.service";
import { AuditService } from "@/services/auditService";

import EventRecordsMixin from "./eventRecords.mixin";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import MaskAlarmDialog from "@/components/alarm-masking/MaskAlarmDialog.vue";
import CreateNewResponse from "@/components/CreateNewResponse.vue";

import { CameraType } from "@/store/site-monitor-cameras/types";
import { FeaturesList } from "@/store/types";
import EventRecordTypes from "@/types/EventRecordTypes";
import MapLayerItemTypes from "@/constants/MapLayerItemTypes";
import { Marker, NewMarker } from "@/types/EnhancedMap";
import { ServerEventType } from "@/store/eventqueue/types";
import {
	EventRecordFilter,
	EventRecord,
	EventDetails,
	EventRecordDetails,
	MaskedAlarm,
	AuditRecordDTO,
	AuditReviewResponse,
	UnlinkCameraDTO
} from "@/store/site-monitor/types";
import ViewsEventBus from "@/services/views/viewsEventBus";
import MessagesList from "@/components/mobile/messages/MessagesList.vue";
import ActiveUserList from "@/components/mobile/messages/ActiveUserList.vue";
import * as messagesApi from "@/services/api.guardChat.service";
import { NewestAt } from '@/components/mobile/messages/NewestAt';
import AlarmAlert from "@/components/AlarmAlert.vue";
import EventTypes from "@/types/EventTypes";

const SessionStore = namespace("sessions");
const SiteMonitor = namespace("siteMonitor");
const SMCameras = namespace("siteMonitorCameras");
const AlarmPoints = namespace("alarmPoints");
const Eventqueue = namespace("eventqueue");
const GuardChat = namespace("guardChat");

// There is no clear unique identity in filters objects.
// In order to prevent multiple messages filter pushes in created hook,
// filter title used as unique prop (assuming no filters with duplicate titles should be added).
// In created hook filters checked for it's presence (by title), and messages filter is not added when found.
const messagesFilterTitle = "Messages";

@Component({
	components: {
		VuePerfectScrollbar: VuePerfectScrollbar,
		MaskAlarmDialog: MaskAlarmDialog,
		CreateNewResponse,
		MessagesList,
		ActiveUserList,
		"alarm-alert": AlarmAlert
	},
	validations: {
		auditNote: {
			required
		}
	}
})
export default class EventRecords extends Mixins(validationMixin, EventRecordsMixin) {
	$refs!: {
		maskAlarmDialog: any;
		fileUploadForm: any;
		fileUploader: any;
		alarmAlert: AlarmAlert;
	};

	@Prop({ default: "siteMonitor/fetchEventRecords" })
	fetchMutation: string = "siteMonitor/fetchEventRecords";

	@Prop(Boolean)
	reviewMode!: boolean;

	@Prop(Boolean)
	eventDetailsAvailable!: Boolean;

	@Inject("viewsEventBus") private eventBus: ViewsEventBus;

	@SessionStore.Getter getSession: any;
	@SessionStore.Action updateSession: any;

	@SiteMonitor.Getter getEventDetails: EventDetails;
	@SiteMonitor.Getter("getAuditService") auditService: AuditService;
	@SiteMonitor.Getter("getCurrentRecordFilter") currentFilter!: null | EventRecordFilter;
	@SiteMonitor.Getter("getFilteredRecords") filteredRecords!: EventRecord[];
	@SiteMonitor.Getter("getEventRecordFilters") filters!: EventRecordFilter[];
	@SiteMonitor.Getter("getEventRecordsAwaitingAckCount") awaitingAck: number;
	@SiteMonitor.Getter("getEventRecordsWithFiles") eventRecordsWithFiles: number[];
	@SMCameras.Getter("getLayouts") layouts!: number[][];
	@SMCameras.Getter("getLayoutIndex") layoutIndex!: number;

	@SiteMonitor.Getter groupID!: number | null;
	@SiteMonitor.Getter maskedAlarms!: MaskedAlarm[];
	@SiteMonitor.Getter getEventRecordsNoFilterRenderIndex: any;

	@SiteMonitor.State(state => state.EventRecords) eventRecords: EventRecord[];

	@SiteMonitor.Mutation setEventRecordFilterRenderIndex: (eventRecordFilter: {
		filterIndex: number,
		renderIndex: number
	}) => void;
	@SiteMonitor.Mutation setEventRecordsNoFilterRenderIndex: (filter: Number) => void;
	@SiteMonitor.Mutation resetEventRecords: () => void;
	@SiteMonitor.Mutation setLocation: (data: {
		latLong: string,
		minElevation: number,
		maxElevation: number}) => void;
	@SiteMonitor.Mutation setSelectedEventRecord: (newEventRecord: EventRecord) => void;
	@SiteMonitor.Mutation setRequestingEventRecords: (isRequestingEventRecord: Boolean) => void;
	@SiteMonitor.Mutation setActivity: () => void;
	@SiteMonitor.Mutation setForceRestoreEventRecord: (eventRecordToRestore: EventRecord) => void;
	@SiteMonitor.Mutation setIsUploadingFile: (isUploadingFile: Boolean) => void;
	@SiteMonitor.Mutation setEventRecordExpanded: (eventRecordExpansion: {
		eventRecordID: number,
		expanded: Boolean
	}) => void;
	@SiteMonitor.Mutation setEventRecordDetails: (eventRecordDetails: {
		eventRecordID: Number,
		eventRecordDetails: EventRecordDetails
	}) => void;
	@SiteMonitor.Mutation addEventRecordFilter: (filter: EventRecordFilter) => void;

	@SiteMonitor.Action setEventRecordFilter: (filter: Number) => void;
	@SiteMonitor.Action createAuditRecord: (auditRecord: AuditRecordDTO) => Promise<EventRecord>;
	@SiteMonitor.Action createReviewRecord: (reviewRecord: AuditRecordDTO) => Promise<AuditReviewResponse>;
	@SiteMonitor.Action fetchEventRecords: (fetchData: { 
		eventID: Number,
		reviewMode: Boolean,
		includeGuardsWithMatchingFilter: Boolean
	}) => Promise<unknown>;
	@SiteMonitor.Action("unlinkCamera") sendUnlinkCameraRequest: (cameraToUnlink: UnlinkCameraDTO) => void;
	@SiteMonitor.Action eventRecordAllAcknowledgement: (eventID: number) => void;
	//@SiteMonitor.Action loadMaskedAlarms: () => void;
	@SiteMonitor.Action ackEventRecord: (eventRecordToAcknowledge: EventRecord) => void;
	@SiteMonitor.Action fetchEventRecordDetails: ({ eventRecordID }) => Promise<EventRecordDetails>;
	@SiteMonitor.Action updateEventRecordLocation: ({ eventRecordId, latLng, elevation }) => Promise<void>;

	@SMCameras.Mutation setAwaitingCamera: any;
	@SMCameras.Mutation setLinkRecord: any;
	@SMCameras.Mutation setSaveLink: any;

	@State(State => State.User.permittedFileTypes) permittedFileTypesPref: string;
	@Getter getTimeZoneAbbr: string;
	@Getter getUserName: string;
	@Getter getUserId: number;
	@Getter getCurrentClipCells: any;
	@Getter getFeature: (feature: string[]) => boolean;
	@Action fetchClipCellsForEvent: any;

	// Getter and setter for toggling the display of the map.
	@SiteMonitor.Action setHideMapFlag: any;
	@SiteMonitor.Getter("getHideMapFlag") hideMapFlag: boolean;

	@GuardChat.Getter("getNumberOfUnread") getNumberOfUnread: (eventId: number) => number;

	@Ref() readonly messagesList: MessagesList;

	public suppressAlerts: boolean = true;
	public previousAlarmCount: number = 0;
	public error: string = "";
	public lastRecord: number = 0;
	public filteredTypeIds: any[] = [];
	public auditNote: string = "";
	public ps: any;
	public updateInterval: number | null = null;
	public ackingAll: boolean = false;
	public fileList: FileList | null = null;
	private firstUpdateEventId: number | null = null;
	private fileListName: string = "";
	private currentDownloadingRecord: number = 0;
	private invalidNote: boolean = false;
	private showErrorAnimation: boolean = false;
	private defaultPermittedFileTypes: string = "avi;mp4;mp3;pdf;png;jpeg;jpg;bmp;csv";

	//Error Modal
	private errorList: string[] = [];
	private errorExists: boolean = false;

	private mapReady: boolean = false;

	@AlarmPoints.Mutation setDisplayResponseId: (responseId: number | null) => void;

	private ended: boolean = false;
	private _renderedFilterIndex: number = -1;
	private _renderedIndex: number = -1;
	private _renderedRecords: any[] = null;

	private rendering: boolean = false;
	private savingRecord: boolean = false;
	private awaitingNew: boolean = true;
	private downloading: boolean = false;
	private clips: any = [];

	//Active user list
	private showActiveUserModal: boolean = false;
    private mentionedUsers: { fullName: string, userID: number }[] = [];

	public get getAllowAlarmMarkers() {
		return get(this.featuresList, ["Alarms", "SiteMonitor", "Map", "AllowAlarmMarkers"]);
	}

	public get getPlaceNoteMarkers() {
		return get(this.featuresList, ["Alarms", "SiteMonitor", "Map", "AllowNoteMarkers"]);
	}

	public get getRestoredIconEnabled() {
		return get(this.featuresList, ["Alarms", "SiteMonitor", "RestoredIcon"]);
	}

	public get fileAttachmentsEnabled() {
		return get(this.featuresList, ["Alarms", "SiteMonitor", "NoteAttachments"]);
	}

	public get alarmPointsEnabled() {
		return get(this.featuresList, ["Alarms", "AlarmPoints"]);
	}

	/** Checks if the supplemental notes feature is enabled */
	public get isSupplementalNotesEnabled() {
		return get(this.featuresList, ["Alarms", "EventSearch", "SupplementalNotes"], false);
	}

	/** Checks if the Expandable event details feature is enabled */
	public get isEventRecordDetailsEnabled() {
		return get(this.featuresList, ["Alarms", "SiteMonitor", "ExpandableEventDetails"], false);
	}

	/** Checks if the AcknowledgeAllEnabled feature is enabled */
	public get isAcknowledgeAllEnabled() {
		return get(this.featuresList, ["Alarms", "SiteMonitor", "AcknowledgeAll"], false);
	}

	/** Checks if the AllClipsEnabled feature is enabled */
	public get isAllClipsEnabled() {
		return get(this.featuresList, ["Alarms", "SiteMonitor", "ShowAllClips"], false);
	}

	private get permittedFileTypes(): string {
		return this.permittedFileTypesPref ? this.permittedFileTypesPref : this.defaultPermittedFileTypes;
	}

	private get permittedFileTypesFormatted(): string {
		return `.${this.permittedFileTypes.replace(/;/g, ",.")}`;
	}

	private created() {
		if (this.eventBus) {
			this.eventBus.$on("set-current-marker", ({ marker, latLng, elevation }) =>
				this.setNewMarkerCb(marker, latLng, elevation)
			);
			this.eventBus.$on("set-map-ready", isReady => (this.mapReady = isReady));
		}

		// check messages feature
		if (
			this.getFeature(["Alarms", "SiteMonitor", "Messages"]) &&
			!this.filters.some(f => f.filterTitle === messagesFilterTitle)
		) {
			this.addEventRecordFilter({
				filterIndex: this.filters.length,
				filterTitle: messagesFilterTitle,
				filterRecordIds: [141, 142],
				showNew: true,
				newCount: 0,
				clearNewOnView: true,
				filterRecords: [],
				renderIndex: 0
			});
		}
	}

	/** Currently rendered event records. Caches, and updates on filter change or render index update */
	public get renderedRecords() {
		let currentFilter = this.currentFilter;
		if (currentFilter == null) {
			let noFilterIndex = this.getEventRecordsNoFilterRenderIndex;
			if (
				this._renderedRecords == null ||
				this._renderedFilterIndex != -1 ||
				this._renderedIndex != noFilterIndex
			) {
				this._renderedFilterIndex = -1;
				this._renderedIndex = noFilterIndex;
				this._renderedRecords = this.filteredRecords.slice(0, noFilterIndex);
			}
		} else {
			if (
				this._renderedRecords == null ||
				this._renderedFilterIndex != currentFilter.filterIndex ||
				this._renderedIndex != currentFilter.renderIndex
			) {
				this._renderedFilterIndex = currentFilter.filterIndex;
				this._renderedIndex = currentFilter.renderIndex;
				this._renderedRecords = this.filteredRecords.slice(0, currentFilter.renderIndex);
			}
		}

		return this._renderedRecords;
	}

	public get parsedEventRecords() {
		return this.filteredRecords.map(x => {
			let y = { ...x };
			if (y.details) {
				y.hasFiles = y.details.includes("{FilesUploaded}");
				y.details = y.details.replace("{FilesUploaded}", "");
			}

			if (this.eventRecordsWithFiles) {
				y.hasFiles = this.eventRecordsWithFiles.includes(y.eventRecordID);
			}

			return y;
		});
	}

	@Watch("eventRecords", { deep: true })
	private setMarkerState(newEventRecords, oldEventRecords) {
		if (newEventRecords && newEventRecords.length > 0 && this.mapReady) {
			this.setMarkers();
		}
	}

	@Watch("mapReady")
	private runMarkerState(mapReady: boolean) {
		if (mapReady && this.eventRecords.length > 0) {
			this.setMarkers();
		}
	}

	private triggerAlarm()
	{
		if(this.$refs.alarmAlert.triggerAlert !== undefined ) {
			this.$refs.alarmAlert.triggerAlert();
		}
	}

	/** If the current filter uses a Priority column  */
	public get hasPriorityColumn() {
		return this.currentFilter == null || this.currentFilter.hasPriorityColumn;
	}

	/** If the current filter shows the Linked Camera column */
	public get hasLinkedCameraColumn() {
		return this.currentFilter == null || this.currentFilter.hasLinkedCameraColumn;
	}

	/** If the current filter shows the "Acknowledge All" bar */
	public get showAckAllBar() {
		return this.filterIndex <= 0 && this.isAcknowledgeAllEnabled;
	}

	/** If we are ready to satr requesting event records */
	public get startRequestingEventRecords() {
		return !this.reviewMode || this.eventDetailsAvailable;
	}

	/** the readable title for the current timezone */
	public get areaTimeZoneTitle() {
		return this.getEventDetails === null ? "" : this.getEventDetails.timeZoneTitle;
	}

	public get selectedFileNames() {
		let fileNamesUploaded = "";

		if (this.fileListHasValue) {
			Array.from(Array(this.fileList.length).keys()).map(x => {
				fileNamesUploaded = `${fileNamesUploaded}${fileNamesUploaded ? ",\n" : "Files attached:\n"}${
					this.fileList[x].name
				}`;
			});
		} else {
			fileNamesUploaded = "No files selected";
		}

		return fileNamesUploaded;
	}

	public get fileListHasValue() {
		if (this.fileList && this.fileList.length) {
			return true;
		}

		return false;
	}

	// Feature flag for map display toggling functionality.
	private get isToggleMapEnabled(): boolean {
		return get(this.featuresList, ["Alarms", "Maps", "ToggleSiteMonitor"]);
	}

	public get layoutHorizontal() {
		return this.layouts[this.layoutIndex][0];
	}

	public get layoutVertical() {
		return this.layouts[this.layoutIndex][1];
	}

	public async mediaList() {
		var auth = this.getSession("2");

		if (this.auditService !== null) {
			await this.auditService.mediaList(auth, this.selectedEventRecordId, true).then((response: any) => {
				this.clips = response.data;
			});
		}
	}

	private get isIncludeGuardsByAlarmQueueFilterEnabled(): boolean {
		return this.getFeature(["Alarms", "Dispatch", "IncludeGuardsByAlarmQueueFilter"]);
	}

	/** On mount, configure records  */
	public async mounted() {
		this.resetEventRecords();
		this.ended = false;
		this.setRequestingEventRecords(false);
		this.update();
		this.setSaveLink(true);
		await this.updateSession({
			resourceId: 2,
			eventId: this.eventid
		});
	}

	/** Stop updating when component is closed  */
	public beforeDestroy() {
		this.ended = true;

		if (this.updateInterval != null) {
			clearInterval(this.updateInterval);
		}
	}

	/** Refresh current event records  */
	public update() {
		if (this.ended) return;
		if (this.isAllClipsEnabled) {
			this.fetchClipCellsForEvent(this.eventid);
			this.mediaList();
		}
		//this.loadMaskedAlarms(this.groupID);
		if (this.fetchMutation != undefined && Number(this.eventid) > 0 && this.startRequestingEventRecords) {
			let update = this.fetchEventRecords({ eventID: this.eventid, 
												  reviewMode: this.reviewMode, 
												  includeGuardsWithMatchingFilter: this.isIncludeGuardsByAlarmQueueFilterEnabled });
			if (update != null) {
				update.then(
					eventRecordCount => {
						if (this.firstUpdateEventId == null || this.firstUpdateEventId != this.eventid) {
							this.firstUpdateEventId = this.eventid;

							if (this.selectedEventRecord) {
								this.$emit("eventRecordSelected", this.selectedEventRecord);
							}

							if (
								!this.reviewMode &&
								this.selectedEventRecord &&
								this.selectedEventRecord.awaitingAcknowledgement
							) {
								this.ackEventRecord(this.selectedEventRecord);
							}
						}

						setTimeout(() => this.update(), 2000);

						if (this.awaitingNew && eventRecordCount > 0) {
							//console.log("Rendering more event records ... ");
							Vue.nextTick(() => {
								this.onScrollToEnd();
							});
						}
						else {
							//check that the queue contains at least one Alarm type - prevents alerts if tours in queue flag added and only tours in queue
							if (this.filteredRecords.some((record: EventRecord) => record.eventRecordTypeID == EventTypes.Alarm)) {
								this.suppressAlerts = false; 
							}
						}
					},
					error => {
						setTimeout(() => this.update(), 2000);
					}
				);
			}
		} else {
			setTimeout(() => this.update(), 2000);
		}
	}

	public filterOn(filter: EventRecordFilter) {
		this.setEventRecordFilter(filter.filterIndex);
	}

	public filterOff() {
		this.setEventRecordFilter(-1);
	}

	public jumpToLocation(eventRecord: EventRecord) {
		if (eventRecord.latLong != "") {
			// if the toggle map feature is on, hide the map.
			if (this.isToggleMapEnabled) {
				this.setHideMapFlag(false);
			}
			this.setLocation({
				latLong: eventRecord.latLong,
				minElevation: eventRecord.minElevation,
				maxElevation: eventRecord.maxElevation
			});
		}
	}

	@Emit("eventRecordSelected")
	public async viewRecord(eventRecord: EventRecord) {
		this.$emit("eventRecordSelected", eventRecord);
		this.setSelectedEventRecord(eventRecord);

		if (!this.reviewMode && eventRecord.awaitingAcknowledgement) {
			this.ackEventRecord(eventRecord);
		}
	}

	public async ackAll() {
		if (this.ackingAll && !this.isAcknowledgeAllEnabled) return;

		if (this.reviewMode) return;

		this.ackingAll = true;
		await this.eventRecordAllAcknowledgement(this.eventid);
		this.ackingAll = false;
	}

	/*** Adds new event record for audit note. Resets activity. */
	public async postAudit() {
		if (!this.savingRecord) {
			this.$v.$touch();
			if (this.$v.auditNote.$error) {
				this.invalidNote = true;
				this.showErrorAnimation = true;
				return;
			}

			this.savingRecord = true;
			let fileNamesUploaded = "";

			const eventRecordId = await this.createRecord();

			if (this.fileListHasValue) {
				await this.setIsUploadingFile(true);
				await this.renewAuditSession();

				Array.from(this.fileList).forEach(async (file, index) => {
					await this.auditService
						.uploadFileForEventRecord(
							this.fileListName,
							file,
							this.getSession("2"),
							this.eventid,
							eventRecordId,
							this.getEventDetails.fileSizeLimit * 300
						)
						.then(async (response: any) => {
							await this.createAuditRecord({
								eventId: this.eventid,
								eventRecordTypeId: 23,
								details: `File ${file.name} upload complete for EventRecordID: ${eventRecordId}`,
								objectId: eventRecordId
							});

							await this.resetFileList(index);
						})
						.catch(async err => {
							await this.resetFileList(index);
							console.log(`Upload failed! Error: ${err.response.data}`);

							this.errorList.push(`Upload failed! Error: ${err.response.data}`);
							this.errorExists = true;
						});
				});
			} else {
				await this.setActivity();
				this.auditNote = "";
				this.savingRecord = false;
			}
		}
	}

	private async createRecord(): Promise<number> {
		if (this.isMessagesFilter) {
			const cmd = {
				eventId: this.eventid,
				messageType: "operator",
				message: this.auditNote,
				mentionedUsers: []
			};
			return await messagesApi.sendMessage(cmd);
		} else {
			const eventRecord = await this.createAuditRecord({
				eventId: this.eventid,
				eventRecordTypeId: 103,
				details: this.auditNote
			});
			return eventRecord.eventRecordID;
		}
	}

	/***
	 * adds new event record for suplimential notes
	 * Used in event search
	 */
	public async postSupplemental() {
		if (!this.savingRecord) {
			this.$v.$touch();
			if (this.$v.auditNote.$error) {
				this.invalidNote = true;
				this.showErrorAnimation = true;
				return;
			}
			this.savingRecord = true;
			const record = await this.createReviewRecord({
				eventId: this.eventid,
				eventRecordTypeId: 17,
				details: `Supplemental Information: ${this.auditNote}`
			});
			if (this.fileListHasValue) {
				await this.setIsUploadingFile(true);
				await this.renewAuditSession();
				Array.from(this.fileList).forEach(async (file, index) => {
					await this.auditService
						.uploadFileForEventRecord(
							this.fileListName,
							file,
							this.getSession("2"),
							this.eventid,
							record.eventRecord.eventRecordID,
							this.getEventDetails.fileSizeLimit * 300
						)
						.then(async (response: any) => {
							await this.createReviewRecord({
								eventId: this.eventid,
								eventRecordTypeId: 23,
								details: `Supplemental Information: File ${file.name} upload complete for EventRecordID: ${record.eventRecord.eventRecordID}`,
								objectId: record.eventRecord.eventRecordID
							});
							await this.resetFileList(index);
						})
						.catch(async err => {
							await this.resetFileList(index);
							console.log(`Upload failed! Error: ${err.response.data}`);
							this.errorList.push(`Upload failed! Error: ${err.response.data}`);
							this.errorExists = true;
						});
				});
			} else {
				await this.setActivity();
				this.auditNote = "";
				this.savingRecord = false;
			}
		}
	}

	public resetErrorModal() {
		this.errorExists = false;
		this.errorList = [];
	}

	private async resetFileList(index) {
		if (index + 1 == this.fileList.length) {
			await this.setActivity();
			this.auditNote = "";
			this.savingRecord = false;
			await this.setIsUploadingFile(false);
			this.fileList = null;
			this.$refs.fileUploadForm.reset();
		}
	}

	private async downloadAttachedFiles(eventRecordId: number) {
		await this.renewAuditSession();
		this.downloading = true;
		this.currentDownloadingRecord = eventRecordId;
		await this.auditService
			.getAllFiles(this.getSession("2"), eventRecordId)
			.then(async (response: any) => {
				const fileName = response.headers["x-suggested-filename"];
				let link = document.createElement("a");
				link.href = window.URL.createObjectURL(response.data);
				link.download = fileName;
				link.click();

				await this.createReviewRecord({
					eventId: this.eventid,
					eventRecordTypeId: 23,
					details: `Files downloaded by user: ${this.getUserName}`
				});
			})
			.catch(err => {
				console.log(`Download failed: ${err.response.data}`);
				this.errorList.push(`Download failed: ${err.response.data}`);
				this.errorExists = true;
			});

		this.downloading = false;
	}

	/**
	 * Opens the Mask Alarm Dialog specified by the name
	 */
	public showMaskAlarmDialog(event: EventRecord) {
		this.$refs.maskAlarmDialog.show(event.eventID, event.objectID, this.maskedAlarmForEvent(event));
	}

	/**
	 * Returns the masked alarm (response) for the given event, if any. Otherwise, this returns undefined.
	 */
	public maskedAlarmForEvent(event: EventRecord) {
		return this.maskedAlarms.find(alarm => alarm.responseID == event.objectID);
	}

	/**
	 * Performs a check to see if the given event record has had it's alarm response masked.
	 */
	public isAlarmMasked(event: EventRecord) {
		return typeof this.maskedAlarmForEvent(event) != "undefined";
	}

	public displayCamera(camera: CameraType) {
		this.setAwaitingCamera({
			objectID: camera.deviceID,
			title: camera.title
		});
	}

	public linkCamera(record: EventRecord) {
		this.setLinkRecord(record);
	}

	public async unlinkCamera(eventRecord: EventRecord, camera: CameraType) {
		await this.sendUnlinkCameraRequest({
			objectId: eventRecord.objectID,
			deviceId: camera.deviceID
		});
		let cameraIndex = eventRecord.attachedCameras.findIndex(
			linkedCamera => linkedCamera.deviceID == camera.deviceID
		);
		eventRecord.attachedCameras.splice(cameraIndex, 1);
	}

	public setForceRestoreOpen(eventRecord: EventRecord) {
		this.setForceRestoreEventRecord(eventRecord);
	}

	onScrollToEnd() {
		if (this.filteredRecords && this.filteredRecords.length == 0) {
			this.awaitingNew = true;
			return;
		}

		if (this.rendering) return;

		this.rendering = true;
		let currentFilter = this.currentFilter;

		if (currentFilter == null) {
			let noFilterIndex = this.getEventRecordsNoFilterRenderIndex;
			let max = this.filteredRecords.length;
			let renderIndex = Math.min(max, noFilterIndex + 50);

			this.setEventRecordsNoFilterRenderIndex(renderIndex);

			this.awaitingNew = noFilterIndex + 50 > max;
		} else {
			let max = currentFilter.filterRecords.length;
			let renderIndex = Math.min(max, currentFilter.renderIndex + 50);

			this.setEventRecordFilterRenderIndex({
				filterIndex: currentFilter.filterIndex,
				renderIndex: renderIndex
			});

			this.awaitingNew = currentFilter.renderIndex + 50 > currentFilter.filterRecords.length;
		}

		Vue.nextTick(() => {
			this.rendering = false;
		});
	}

	private filesChanged(name, files: FileList) {
		const invalidFiles = Array.from(files).filter(file => file.name.length > 240);

		if (invalidFiles.length > 0) {
			this.errorList.push(
				`${invalidFiles.length} File(s) are exceeding the character limit. The limit is 240 characters.`
			);
		}

		if (
			Array.from(files).reduce((acc, file) => acc + file.size, 0) >=
			this.getEventDetails.fileSizeLimit * 1000000
		) {
			this.errorList.push(`Maximum upload size is ${this.getEventDetails.fileSizeLimit}MB`);
		}
			
		const permittedFileTypes: string[] = this.permittedFileTypes.split(";").map((type) => {
			return type.trim();
		});

		if(
			Array.from(files).some(file => !permittedFileTypes.includes(file.name.substring(file.name.lastIndexOf('.') + 1, file.name.length)))
		) {
			this.errorList.push("File type is not permitted for upload");
		}

		if (this.errorList.length > 0) {
			this.errorExists = true;
		} else {
			this.fileListName = name;
			this.fileList = files;
		}

		this.$refs.fileUploader.files = null;
		// this.$refs.fileUploader.value = this.fileList;
	}

	private async renewAuditSession() {
		await this.updateSession({
			resourceId: 2,
			eventId: this.eventid
		});
	}

	private checkCurrentlyDownloading(eventRecordId: number) {
		return this.currentDownloadingRecord == eventRecordId && this.downloading;
	}

	/** Expands  the event details for a record */
	private async showEventRecordDetails(eventRecord: EventRecord) {
		// Get the event record details for the event record
		let details = await this.fetchEventRecordDetails({ eventRecordID: eventRecord.eventRecordID });

		this.setEventRecordDetails({
			eventRecordID: eventRecord.eventRecordID,
			eventRecordDetails: details
		});

		this.setEventRecordExpanded({
			eventRecordID: eventRecord.eventRecordID,
			expanded: true
		});
	}

	/** Collapses the event details for a record */
	private async hideEventRecordDetails(eventRecord: EventRecord) {
		this.setEventRecordExpanded({
			eventRecordID: eventRecord.eventRecordID,
			expanded: false
		});
	}

	public cameraIconColor(event: any, i: number, j: number) {
		var found = false;
		try {
			this.clips.forEach(clip => {
				if (clip.EventRecordId == event.eventRecordID) {
					this.getCurrentClipCells.forEach(clipCells => {
						if (JSON.parse(clipCells.contents).clip?.fileIdentifier == clip?.FileIdentifier) {
							if ((i - 1) * this.layoutHorizontal + j == clipCells.cellIndex) {
								found = true;
							}
						}
					});
				}
			});
		} catch (e) {
			return "greyCameraIndicator";
		}
		return found ? "redCameraIndicator" : "greyCameraIndicator";
	}

	private get hasMapPinIconPref(): boolean {
		return this.getEventDetails && this.getEventDetails.mapPinIcon ? true : false;
	}

	private buildMarker(
		event: any,
		setLocation: boolean = false,
		setSpotlight: boolean = false,
		state: string = ""
	): NewMarker {
		let newMarker: NewMarker = {
			setSpotlight: setSpotlight,
			state: state
		};

		newMarker.marker = {
			eventRecordId: event.eventRecordID,
			map: null,
			latLong: setLocation ? event.location ?? null : null,
			mapLayerItemId: 0,
			mapLayerItemTypeId: 0,
			objectId: event.eventRecordID,
			title: event.details,
			regionPath: null,
			minElevation: event.elevation,
			maxElevation: event.elevation,
			visible: true
		};

		switch (event.eventRecordTypeID) {
			case EventRecordTypes.Alarm:
				newMarker.marker.mapLayerItemTypeId = MapLayerItemTypes.Alarm;
				break;
			case EventRecordTypes.OperatorNote:
				newMarker.marker.mapLayerItemTypeId = MapLayerItemTypes.Note;
				break;
			default:
				newMarker.marker.mapLayerItemTypeId = MapLayerItemTypes.Alarm;
				break;
		}

		return newMarker;
	}

	private setNewMarker(event: any): void {
		this.eventBus.$emit(
			"set-new-marker",
			this.buildMarker(event, false, this.isInitAlarm(event), this.isInitAlarm(event) ? "processing" : "")
		);
	}

	private isInitAlarm(event: any): boolean {
		return (
			event.eventRecordTypeID == EventRecordTypes.Alarm &&
			this.eventRecords &&
			this.eventRecords.findIndex(x => x.eventRecordID == event.eventRecordID) == 0
		);
	}

	private async setNewMarkerCb(marker: Marker, latLng: any, elevation: number) {
		await this.updateEventRecordLocation({ eventRecordId: marker.eventRecordId, latLng, elevation });
	}

	private setMarkers() {
		if (!this.eventRecords || this.eventRecords.length == 0) return;
		this.eventRecords.forEach((event, index: number) => {
			if (index == 0 && (this.getAllowAlarmMarkers || this.hasMapPinIconPref)) {
				if (event.latLong == null || event.latLong == "") return;
				this.eventBus.$emit("set-marker", this.buildMarker(event, true, this.isInitAlarm(event), "processing"));
				return;
			}

			if (
				this.acceptsMarkerPlacement(event) &&
				index > 0 &&
				(this.getPlaceNoteMarkers || this.hasMapPinIconPref)
			) {
				if (event.latLong == null || event.latLong == "") return;
				this.eventBus.$emit("set-marker", this.buildMarker(event, true, this.isInitAlarm(event)));
			}
		});
	}

	private acceptsMarkerPlacement(event: any): boolean {
		switch (event.eventRecordTypeID) {
			case EventRecordTypes.Alarm:
				return true;
			case EventRecordTypes.OperatorNote:
				return true;
			default:
				return false;
		}
	}

	private get isMessagesFilter(): boolean {
		return this.filters[this.filterIndex] && this.filters[this.filterIndex].filterTitle === messagesFilterTitle;
	}

	private urlIsAnchorTag(eventRecord: EventRecord): boolean {
		return (
			eventRecord &&
			eventRecord.eventRecordDetails &&
			eventRecord.eventRecordDetails.url &&
			eventRecord.eventRecordDetails.url.length > 0 &&
			eventRecord.eventRecordDetails.url.includes("<a href") &&
			!eventRecord.eventRecordDetails.url.includes("<script")
		);
	}

	private updatedTask: (() => void) | undefined;

	@Watch("isMessagesFilter")
	private onMessagesVisibilityChange(newVal, oldVal) {
		if (newVal) {
			this.updatedTask = () => { this.messagesList.restoreScrolling(); }
		} else {
			this.messagesList.storeScrolling();
			this.updatedTask = undefined;
		}
	}

	private updated() {
		if (this.updatedTask) {
			this.updatedTask();
			this.updatedTask = undefined;
		}
	}

	private getNewCount(filter: EventRecordFilter) {
		if (filter.filterTitle === messagesFilterTitle) {
			return this.getNumberOfUnread(this.eventid);
		} else {
			if (filter.filterTitle === "Alarms" && filter.newCount > this.previousAlarmCount) {
				this.previousAlarmCount = filter.newCount;
				this.triggerAlarm();
			}
			return filter.newCount;
		}
	}

	private get newestMessagesAt(): NewestAt {
		return this.getFeature(["Alarms", "SiteMonitor", "ChatMessagesNewestOnTop"]) ? "top" : "bottom";
	}

	@Watch("auditNote")
    private onAuditNoteUpdated(newValue: string) {
        if (newValue.endsWith("@")) {
            this.showActiveUserModal = true;
        }
    }

    public onActiveUserRowClick(clickedUser) {
        this.auditNote = this.auditNote + clickedUser.fullName;
        this.mentionedUsers.push(clickedUser);
        this.showActiveUserModal = false;
    }

	public onActiveUserHidden() {
        this.showActiveUserModal = false;
    }

	private checkMessageForUserNames() {
        this.mentionedUsers = this.mentionedUsers.filter(u => this.auditNote.includes(u.fullName));
    }
}
